import { LitElement, html, css } from 'lit';
import { getConfig } from '../config.js';

/**
 * Normalise et valide le format YYYY-MM
 */
function parseMonthYearValue(str) {
  if (!str) return null;
  const parts = str.split('-');
  if (parts.length === 2) {
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    if (!isNaN(y) && !isNaN(m) && m >= 0 && m <= 11) {
      return { year: y, month: m };
    }
  }
  return null;
}

function toMonthYearValueString(year, month) {
  const m = String(month + 1).padStart(2, '0');
  return `${year}-${m}`;
}

function formatMonthYear(year, month, formatPattern) {
  // Traduit ex: 5 (Juin) et 2026 -> "Juin 2026" ou "06/2026"
  const mIndex = month;
  const monthsFull = getConfig('datepicker', 'months');
  const monthsShort = getConfig('datepicker', 'monthsShort');
  
  const mFull = monthsFull[mIndex];
  const mShort = monthsShort[mIndex];
  const mNum = String(mIndex + 1).padStart(2, '0');
  const y = String(year);

  // Essayer d'interpréter le pattern
  if (formatPattern === 'MM/YYYY') {
    return `${mNum}/${y}`;
  }
  // Par défaut: "Mois YYYY" (ex: "Mai 2026")
  return `${mFull} ${y}`;
}

function parseMonthYearInput(str, formatPattern) {
  if (!str) return null;
  const monthsFull = getConfig('datepicker', 'months').map(m => m.toLowerCase());
  const monthsShort = getConfig('datepicker', 'monthsShort').map(m => m.toLowerCase());
  const cleaned = str.trim().toLowerCase();

  // Test simple format MM/YYYY ou MM-YYYY
  if (cleaned.includes('/') || cleaned.includes('-')) {
    const sep = cleaned.includes('/') ? '/' : '-';
    const parts = cleaned.split(sep);
    if (parts.length === 2) {
      let m = parseInt(parts[0], 10) - 1;
      let y = parseInt(parts[1], 10);
      if (!isNaN(m) && !isNaN(y) && m >= 0 && m <= 11) {
        return { year: y, month: m };
      }
    }
  }

  // Test format textuel ex: "Mai 2026" ou "Jan 2026"
  const parts = cleaned.split(/\s+/);
  if (parts.length === 2) {
    let m = monthsFull.indexOf(parts[0]);
    if (m === -1) m = monthsShort.indexOf(parts[0]);
    let y = parseInt(parts[1], 10);
    
    if (m !== -1 && !isNaN(y)) {
      return { year: y, month: m };
    }
  }

  return null;
}

/**
 * Composant <ui-monthpicker>
 * Permet de sélectionner uniquement un mois et une année dans une grille 3x4
 */
export class UIMonthPicker extends LitElement {
  static formAssociated = true;

  static properties = {
    name: { type: String },
    value: { type: String, reflect: true }, // Format ISO YYYY-MM
    placeholder: { type: String },
    disabled: { type: Boolean, reflect: true },
    required: { type: Boolean, reflect: true },
    min: { type: String }, // Format YYYY-MM
    max: { type: String }, // Format YYYY-MM
    label: { type: String, reflect: true },
    
    // États internes
    isOpen: { type: Boolean, state: true },
    viewMode: { type: String, state: true }, // 'months', 'years'
    currentYear: { type: Number, state: true },
    inputValue: { type: String, state: true }
  };

  static styles = css`
    :host {
      display: inline-block;
      width: 100%;
      position: relative;
      font-family: var(--ui-font-family);
      font-size: var(--ui-font-size-base);
      color: var(--ui-text);
    }

    .ui-label {
      display: block;
      margin-bottom: 6px;
      font-size: var(--ui-font-size-sm);
      font-weight: 500;
      color: var(--ui-text-muted);
      text-align: left;
    }

    .monthpicker-container {
      position: relative;
      width: 100%;
    }

    /* Le Header (Input principal) */
    .monthpicker-header {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
    }

    .monthpicker-input {
      width: 100%;
      height: var(--ui-input-height);
      padding: 0 var(--ui-padding-x);
      padding-right: 40px;
      background-color: var(--ui-surface);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-md);
      font-family: inherit;
      font-size: inherit;
      color: inherit;
      box-shadow: var(--ui-shadow-sm);
      transition: var(--ui-transition-fast);
      outline: none;
      box-sizing: border-box;
    }

    .monthpicker-input:hover:not(:disabled) {
      border-color: var(--ui-border-hover);
      background-color: var(--ui-surface-hover);
    }

    .monthpicker-input:focus:not(:disabled) {
      border-color: var(--ui-border-focus);
      background-color: var(--ui-surface);
      box-shadow: 0 0 0 4px var(--ui-focus-ring);
    }

    .monthpicker-input:disabled {
      cursor: not-allowed;
      opacity: 0.6;
      background-color: var(--ui-surface-active);
    }

    .monthpicker-icon {
      position: absolute;
      right: var(--ui-padding-x);
      top: 50%;
      transform: translateY(-50%);
      color: var(--ui-text-muted);
      pointer-events: none;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: var(--ui-transition-fast);
    }

    .monthpicker-container.open .monthpicker-icon {
      color: var(--ui-primary);
    }

    /* Le Panel Popup */
    .monthpicker-panel {
      position: absolute;
      top: calc(100% + 6px);
      left: 0;
      width: 280px;
      padding: 16px;
      background-color: color-mix(in srgb, var(--ui-surface) 90%, transparent);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-lg);
      box-shadow: var(--ui-shadow-popover);
      z-index: var(--ui-z-popover);
      transform-origin: top;
      opacity: 0;
      transform: scale(0.95);
      pointer-events: none;
      transition: opacity var(--ui-transition-fast), transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
      box-sizing: border-box;
    }

    .monthpicker-panel.open {
      opacity: 1;
      transform: scale(1);
      pointer-events: auto;
    }

    /* En-tête du volet (navigation année) */
    .panel-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 16px;
    }

    .year-title {
      display: flex;
      align-items: center;
    }

    .header-btn {
      border: none;
      background: transparent;
      color: var(--ui-text);
      font-family: inherit;
      font-size: var(--ui-font-size-base);
      font-weight: 600;
      cursor: pointer;
      outline: none;
      padding: 4px 8px;
      border-radius: var(--ui-radius-sm);
      transition: var(--ui-transition-fast);
    }

    .header-btn:hover {
      background-color: var(--ui-surface-hover);
      color: var(--ui-primary);
    }

    .nav-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-sm);
      background-color: transparent;
      color: var(--ui-text);
      cursor: pointer;
      transition: var(--ui-transition-fast);
    }

    .nav-btn:hover {
      background-color: var(--ui-surface-hover);
      border-color: var(--ui-border-hover);
      color: var(--ui-primary);
    }

    /* Grille 3x4 pour les 12 mois */
    .months-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
    }

    .month-cell {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 42px;
      border-radius: var(--ui-radius-sm);
      cursor: pointer;
      font-size: var(--ui-font-size-base);
      font-weight: 500;
      transition: var(--ui-transition-fast);
      text-transform: capitalize;
    }

    .month-cell:hover:not(.disabled):not(.selected) {
      background-color: var(--ui-surface-hover);
      color: var(--ui-primary);
    }

    .month-cell.selected {
      background-color: var(--ui-primary);
      color: var(--ui-text-on-primary);
      font-weight: 600;
    }

    .month-cell.current-month:not(.selected) {
      border: 1.5px solid var(--ui-primary);
      color: var(--ui-primary);
      font-weight: 600;
    }

    .month-cell.disabled {
      cursor: not-allowed;
      opacity: 0.25;
      background-color: transparent;
    }

    /* Footer avec bouton Ce mois-ci */
    .panel-footer {
      margin-top: 14px;
      padding-top: 8px;
      border-top: 1px solid var(--ui-border);
      display: flex;
      justify-content: center;
    }

    .btn-this-month {
      width: 100%;
      height: 32px;
      border: 1px solid var(--ui-primary);
      border-radius: var(--ui-radius-sm);
      background-color: transparent;
      color: var(--ui-primary);
      font-size: var(--ui-font-size-sm);
      font-weight: 600;
      cursor: pointer;
      transition: var(--ui-transition-fast);
    }

    .btn-this-month:hover {
      background-color: var(--ui-primary);
      color: var(--ui-text-on-primary);
    }

    /* Backdrop pour version mobile */
    .backdrop {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(4px);
      z-index: calc(var(--ui-z-popover) - 1);
    }

    /* Style Responsive Mobile - Bottom Sheet */
    @media (max-width: 576px) {
      .monthpicker-container.open .backdrop {
        display: block;
      }

      .monthpicker-panel {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        top: auto;
        width: 100%;
        max-width: 100%;
        border-radius: var(--ui-radius-lg) var(--ui-radius-lg) 0 0;
        box-shadow: 0 -10px 25px rgba(0,0,0,0.15);
        transform: translateY(100%);
        transform-origin: bottom;
        transition: transform var(--ui-transition-normal) cubic-bezier(0.25, 0.8, 0.25, 1), opacity var(--ui-transition-normal);
        z-index: var(--ui-z-popover);
        box-sizing: border-box;
      }

      .monthpicker-container.open .monthpicker-panel {
        transform: translateY(0);
        opacity: 1;
      }

      .monthpicker-panel::before {
        content: '';
        display: block;
        width: 40px;
        height: 4px;
        background-color: var(--ui-border);
        border-radius: var(--ui-radius-full);
        margin: -8px auto 16px auto;
      }
    }
  `;

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.name = '';
    this.value = '';
    this.placeholder = 'Sélectionner un mois...';
    this.disabled = false;
    this.required = false;
    this.min = '';
    this.max = '';
    this.label = '';

    // États initiaux
    this.isOpen = false;
    this.viewMode = 'months'; // 'months' ou 'years'
    this.inputValue = '';
    
    const today = new Date();
    this.currentYear = today.getFullYear();

    this._handleOutsideClick = this._handleOutsideClick.bind(this);
  }

  get validity() { return this.internals.validity; }
  get validationMessage() { return this.internals.validationMessage; }
  get willValidate() { return this.internals.willValidate; }
  checkValidity() { return this.internals.checkValidity(); }
  reportValidity() { return this.internals.reportValidity(); }

  updated(changedProperties) {
    if (changedProperties.has('value') || changedProperties.has('required')) {
      this.internals.setFormValue(this.value);
      this._updateValidity();
    }
  }

  _updateValidity() {
    const hasValue = this.value && this.value.trim() !== '';
    if (this.required && !hasValue) {
      this.internals.setValidity({ valueMissing: true }, 'Veuillez sélectionner un mois.', this.shadowRoot.querySelector('.monthpicker-input'));
    } else {
      this.internals.setValidity({});
    }
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this._handleOutsideClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this._handleOutsideClick);
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('value')) {
      const parsed = parseMonthYearValue(this.value);
      if (parsed) {
        this.inputValue = formatMonthYear(parsed.year, parsed.month, 'FULL_TEXT');
        this.currentYear = parsed.year;
      } else {
        this.inputValue = '';
      }
    }
  }

  _handleOutsideClick(e) {
    if (this.isOpen && !this.contains(e.target) && !e.composedPath().includes(this)) {
      this._closePanel();
    }
  }

  _togglePanel() {
    if (this.disabled) return;
    if (this.isOpen) {
      this._closePanel();
    } else {
      this._openPanel();
    }
  }

  _openPanel() {
    this.isOpen = true;
    this.viewMode = 'months';
    const parsed = parseMonthYearValue(this.value);
    if (parsed) {
      this.currentYear = parsed.year;
    } else {
      this.currentYear = new Date().getFullYear();
    }
  }

  _closePanel() {
    this.isOpen = false;
  }

  _handleInput(e) {
    this.inputValue = e.target.value;
    const parsed = parseMonthYearInput(this.inputValue, 'FULL_TEXT');

    if (parsed) {
      if (this._isMonthDisabled(parsed.year, parsed.month)) {
        return;
      }
      const newISO = toMonthYearValueString(parsed.year, parsed.month);
      if (newISO !== this.value) {
        this.value = newISO;
        this.currentYear = parsed.year;
        this._dispatchChangeEvent(newISO);
      }
    }
  }

  _handleBlur() {
    const parsed = parseMonthYearValue(this.value);
    if (parsed) {
      this.inputValue = formatMonthYear(parsed.year, parsed.month, 'FULL_TEXT');
    } else {
      this.inputValue = '';
    }
  }

  _selectMonth(monthIndex) {
    if (this._isMonthDisabled(this.currentYear, monthIndex)) return;

    const newISO = toMonthYearValueString(this.currentYear, monthIndex);
    this.value = newISO;
    this._closePanel();
    this._dispatchChangeEvent(newISO);
  }

  _selectThisMonth() {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth();

    if (this._isMonthDisabled(y, m)) return;

    const newISO = toMonthYearValueString(y, m);
    this.value = newISO;
    this.currentYear = y;
    this._closePanel();
    this._dispatchChangeEvent(newISO);
  }

  _dispatchChangeEvent(isoValue) {
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    
    const parsed = parseMonthYearValue(isoValue);
    const label = parsed ? formatMonthYear(parsed.year, parsed.month, 'FULL_TEXT') : '';

    this.dispatchEvent(new CustomEvent('ui-change', {
      detail: { value: isoValue, formatted: label },
      bubbles: true,
      composed: true
    }));
  }

  _isMonthDisabled(year, monthIndex) {
    if (this.min) {
      const minParts = parseMonthYearValue(this.min);
      if (minParts) {
        if (year < minParts.year) return true;
        if (year === minParts.year && monthIndex < minParts.month) return true;
      }
    }
    if (this.max) {
      const maxParts = parseMonthYearValue(this.max);
      if (maxParts) {
        if (year > maxParts.year) return true;
        if (year === maxParts.year && monthIndex > maxParts.month) return true;
      }
    }
    return false;
  }

  _prevYear() {
    if (this.viewMode === 'months') {
      this.currentYear -= 1;
    } else {
      this.currentYear -= 12; // recule d'une décennie
    }
  }

  _nextYear() {
    if (this.viewMode === 'months') {
      this.currentYear += 1;
    } else {
      this.currentYear += 12; // avance d'une décennie
    }
  }

  _selectYear(year) {
    if (this._isYearDisabled(year)) return;
    this.currentYear = year;
    this.viewMode = 'months';
  }

  _isYearDisabled(year) {
    if (this.min) {
      const minParts = parseMonthYearValue(this.min);
      if (minParts && year < minParts.year) return true;
    }
    if (this.max) {
      const maxParts = parseMonthYearValue(this.max);
      if (maxParts && year > maxParts.year) return true;
    }
    return false;
  }

  _handleKeyDown(e) {
    if (e.key === 'Escape' && this.isOpen) {
      e.preventDefault();
      this._closePanel();
    }
  }

  render() {
    const monthsShort = getConfig('datepicker', 'monthsShort');
    const today = new Date();
    const parsedValue = parseMonthYearValue(this.value);
    
    // Grille d'années (12 ans centrés autour de currentYear)
    const yearsGridRange = Array.from({ length: 12 }, (_, i) => this.currentYear - 5 + i);

    return html`
      ${this.label ? html`<label class="ui-label" part="label">${this.label}</label>` : ''}
      <div class="monthpicker-container ui-component ${this.isOpen ? 'open' : ''}">
        <!-- Soumission automatique gérée via ElementInternals -->

        <!-- Backdrop mobile -->
        <div class="backdrop" @click="${this._closePanel}"></div>

        <!-- En-tête avec input -->
        <div class="monthpicker-header">
          <input 
            type="text" 
            inputmode="none"
            class="monthpicker-input" part="input" 
            placeholder="${this.placeholder}"
            .value="${this.inputValue}"
            ?disabled="${this.disabled}"
            @focus="${this._openPanel}"
            @input="${this._handleInput}"
            @blur="${this._handleBlur}"
            @keydown="${this._handleKeyDown}"
          >
          <span class="monthpicker-icon" @click="${this._togglePanel}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 18px; height: 18px;">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
              <path d="M8 14h.01"></path>
              <path d="M12 14h.01"></path>
              <path d="M16 14h.01"></path>
              <path d="M8 18h.01"></path>
              <path d="M12 18h.01"></path>
              <path d="M16 18h.01"></path>
            </svg>
          </span>
        </div>

        <!-- Volet de sélection -->
        <div class="monthpicker-panel ${this.isOpen ? 'open' : ''}">
          <div class="panel-header">
            <button type="button" class="nav-btn" @click="${this._prevYear}">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 14px; height: 14px;">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            
            <div class="year-title">
              ${this.viewMode === 'months' ? html`
                <button type="button" class="header-btn" @click="${() => this.viewMode = 'years'}">${this.currentYear}</button>
              ` : html`
                <button type="button" class="header-btn" @click="${() => this.viewMode = 'months'}">${this.currentYear - 5} - ${this.currentYear + 6}</button>
              `}
            </div>
            
            <button type="button" class="nav-btn" @click="${this._nextYear}">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 14px; height: 14px;">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>

          ${this.viewMode === 'months' ? html`
            <div class="months-grid">
              ${monthsShort.map((m, idx) => {
                const isSelected = parsedValue && 
                                   parsedValue.month === idx && 
                                   parsedValue.year === this.currentYear;
                                   
                const isCurrent = today.getMonth() === idx && 
                                  today.getFullYear() === this.currentYear;
                                  
                const isDisabled = this._isMonthDisabled(this.currentYear, idx);

                return html`
                  <div 
                    class="month-cell 
                      ${isSelected ? 'selected' : ''} 
                      ${isCurrent ? 'current-month' : ''} 
                      ${isDisabled ? 'disabled' : ''}"
                    @click="${() => this._selectMonth(idx)}"
                  >
                    ${m}
                  </div>
                `;
              })}
            </div>
          ` : html`
            <div class="months-grid">
              ${yearsGridRange.map(y => {
                const isSelected = parsedValue && parsedValue.year === y;
                const isCurrent = today.getFullYear() === y;
                const isDisabled = this._isYearDisabled(y);

                return html`
                  <div 
                    class="month-cell 
                      ${isSelected ? 'selected' : ''} 
                      ${isCurrent ? 'current-month' : ''} 
                      ${isDisabled ? 'disabled' : ''}"
                    @click="${() => this._selectYear(y)}"
                  >
                    ${y}
                  </div>
                `;
              })}
            </div>
          `}

          <div class="panel-footer">
            <button type="button" class="btn-this-month" @click="${this._selectThisMonth}">Ce mois-ci</button>
          </div>
        </div>
      </div>
    `;
  }
}

if (!customElements.get('ui-monthpicker')) {
  customElements.define('ui-monthpicker', UIMonthPicker);
}
